/**
 * Men do not stand inside one another (02 §9). Reported in play: "enemies can walk over each other".
 *
 * Every mover resolves against the STATICS - walls, buildings, wrecks (`resolveMovement`) - and
 * against nothing else, so two soldiers sent to the same place converged on it and finished as one
 * silhouette with two shadows.
 *
 * The first version of this was a hard constraint: part every overlapping pair by half the overlap
 * each, straight down the line between them. That traded one bug for a worse one - reported next
 * time out as "four soldiers all trying to walk against each other, so nobody was moving". Two men
 * walking head-on have their overlap pointing ALONG their line of travel, so parting them down it
 * opposes their walk exactly; `travel` puts them back the following tick and neither ever arrives.
 *
 * So it steers rather than constrains. The push is soft - a fraction of the overlap per tick, which
 * a walking man out-paces - and it carries a sideways component, so a man meeting another steps
 * ROUND him instead of shoving at him. Which way he steps is fixed by his id, so two men never
 * choose the same side and the whole thing stays reproducible.
 */

import type { Ctx } from '../ctx';
import { resolveMovement } from '../statics';
import { ENEMY } from '../tuning';
import { type Enemy, TICK_RATE } from '../types';
import { isAutomated } from './specials/kinds';

/**
 * How much of the overlap is taken out per tick. Soft on purpose: at walking pace a man covers far
 * more than this in a tick, so he can always walk out of a press rather than be held in it. A hard
 * 1 is what deadlocked four men in a doorway.
 */
const FIRMNESS = 0.34;
/**
 * How much of the push goes SIDEWAYS. This is the whole fix for a head-on meeting: without it the
 * only force is back the way he came, and two men walking at each other simply stall.
 */
const SIDESTEP = 0.8;
/**
 * The hard ceiling on a push, and the reason a press can always be walked out of: half a stride.
 * A push bigger than a stride is a wall - a man shoved 0.066 m while he walks 0.025 m never leaves,
 * which is precisely what "nobody was moving" was. Whatever the overlap, the walk wins.
 */
const MAX_PUSH = (ENEMY.walkSpeed / TICK_RATE) * 0.5;
/** Below this the two are treated as exactly on top of each other and parted along x. */
const COINCIDENT = 1e-4;

/**
 * Who takes part. The dead lie where they fell and are walked over, as bodies are. A machine is
 * immovable by a man - a lorry does not give way, it runs him down (`runOver`) - and a mini-bunker
 * is a hole in the ground. Men in vehicles are not in `world.enemies` at all.
 */
function shoves(e: Enemy): boolean {
  return e.state !== 'dead' && !isAutomated(e.kind);
}

/**
 * Nudge overlapping men apart and round each other. Each pair is parted evenly, so no man is
 * privileged by his place in the array, and every figure comes from the pair itself - there is no
 * randomness here and a replay reproduces it exactly.
 */
export function separateEnemies(ctx: Ctx): void {
  const all = ctx.world.enemies;
  const reach = ENEMY.radius * 2;
  let parted = false;

  for (let i = 0; i < all.length; i++) {
    const a = all[i];
    if (!a || !shoves(a)) continue;
    for (let j = i + 1; j < all.length; j++) {
      const b = all[j];
      if (!b || !shoves(b)) continue;
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const d2 = dx * dx + dz * dz;
      if (d2 >= reach * reach) continue;
      parted = true;
      const d = Math.sqrt(d2);
      // Two men on the same spot have no direction to part along: use x, deterministically.
      const ux = d < COINCIDENT ? 1 : dx / d;
      const uz = d < COINCIDENT ? 0 : dz / d;
      // The sidestep: perpendicular to the line between them. The lower id goes one way and the
      // higher the other, so they pass on opposite sides instead of both choosing the same gap.
      const side = (a.id & 1) === 0 ? 1 : -1;
      const push = Math.min(MAX_PUSH, ((reach - d) / 2) * FIRMNESS);
      const px = ux + -uz * SIDESTEP * side;
      const pz = uz + ux * SIDESTEP * side;
      const len = Math.sqrt(px * px + pz * pz) || 1;
      a.x -= (px / len) * push;
      a.z -= (pz / len) * push;
      b.x += (px / len) * push;
      b.z += (pz / len) * push;
    }
  }

  if (!parted) return;
  // Parting two men can put one inside a wall; the statics have the last word, as they always do.
  for (const e of all) {
    if (!shoves(e)) continue;
    const seated = resolveMovement(ctx.statics, e.x, e.z, ENEMY.radius);
    e.x = seated.x;
    e.z = seated.z;
  }
}
